import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, RotateCcw, Cpu, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CAN_MANAGE = ["super_admin", "hr", "manager", "it"];

const ASSET_TYPES = ["Monitor", "CPU", "Headset", "Keyboard", "Camera"];

const EMPTY_FORM = {
  type: "",
  serial: "",
  coder_name: "",
  employee_id: "",
  given_date: "",
  return_date: "",
  notes: "",
  status: "assigned",
};

export default function Assets() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);

  const [refresh, setRefresh] = useState(0);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const reset = () => setForm(EMPTY_FORM);

  const submit = async () => {
    if (!form.type) { toast.error("Asset type is required"); return; }
    if (!form.serial) { toast.error("Serial number is required"); return; }
    setSubmitting(true);
    try {
      // Map new fields onto existing backend fields so reports stay backward-
      // compatible: assigned_to <- employee_id, assigned_name <- coder_name,
      // assigned_on <- given_date.
      const payload = {
        ...form,
        assigned_to: form.employee_id || null,
        assigned_name: form.coder_name || null,
        assigned_on: form.given_date || null,
      };
      await api.post("/assets", payload);
      toast.success("Asset added");
      setOpen(false);
      reset();
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  const returnAsset = async (a) => {
    await api.post(`/assets/${a.id}/return`);
    toast.success("Asset returned");
    setRefresh((v) => v + 1);
  };

  return (
    <div data-testid="assets-page">
      <PageHeader
        title="Asset Management"
        subtitle="Track monitors, CPUs, headsets, keyboards and cameras assigned to coders."
        action={
          canManage ? (
            <Button data-testid="add-asset" onClick={() => setOpen(true)} className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]">
              <Plus className="h-4 w-4 mr-1.5" /> Add asset
            </Button>
          ) : null
        }
      />

      <DataTable
        testId="assets-table"
        endpoint="/assets"
        refreshKey={refresh}
        columns={[
          { key: "type", label: "Asset Type" },
          { key: "serial", label: "Serial Number", mono: true },
          { key: "coder_name", label: "Coder Name", render: (r) =>
            r.coder_name || r.assigned_name || <span className="text-gray-400">Unassigned</span>
          },
          { key: "employee_id", label: "Employee ID", mono: true, render: (r) =>
            r.employee_id || r.assigned_to || "—"
          },
          { key: "given_date", label: "Asset Given Date", render: (r) =>
            r.given_date || r.assigned_on || "—"
          },
          { key: "return_date", label: "Return Date", render: (r) => r.return_date || "—" },
          { key: "status", label: "Status", badge: true },
          { key: "actions", label: "Actions", render: (r) => {
            if (r.status !== "assigned") return "—";
            return (
              <Button
                data-testid={`return-asset-${r.id}`}
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); returnAsset(r); }}
                className="h-8"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Return
              </Button>
            );
          }},
        ]}
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-lg" data-testid="add-asset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cpu className="h-5 w-5 text-[#F26B3A]" /> Add asset
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            {/* Asset Type — required, gates the Serial field */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Asset Type *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="asset-type-select" className="mt-1.5">
                  <SelectValue placeholder="Select asset type" />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map((t) => (
                    <SelectItem
                      key={t}
                      value={t}
                      data-testid={`asset-type-${t.toLowerCase()}`}
                    >
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="asset-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="under_repair">Under repair</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Serial Number — only shown after Asset Type is picked, per spec */}
            {form.type && (
              <div className="col-span-2" data-testid="asset-serial-row">
                <Label className="text-xs uppercase tracking-wider text-gray-500">
                  Serial Number * <span className="text-gray-400 normal-case">({form.type})</span>
                </Label>
                <Input
                  data-testid="asset-serial"
                  className="mt-1.5"
                  value={form.serial}
                  onChange={(e) => setForm({ ...form, serial: e.target.value })}
                  placeholder="SN-XXXXXX"
                />
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Coder Name</Label>
              <Input
                data-testid="asset-coder-name"
                className="mt-1.5"
                value={form.coder_name}
                onChange={(e) => setForm({ ...form, coder_name: e.target.value })}
                placeholder="e.g. Eliza Park"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Employee ID</Label>
              <Input
                data-testid="asset-emp-id"
                className="mt-1.5"
                value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                placeholder="EMP-0005"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Asset Given Date</Label>
              <Input
                data-testid="asset-given-date"
                type="date"
                className="mt-1.5"
                value={form.given_date}
                onChange={(e) => setForm({ ...form, given_date: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Return Date</Label>
              <Input
                data-testid="asset-return-date"
                type="date"
                className="mt-1.5"
                value={form.return_date}
                onChange={(e) => setForm({ ...form, return_date: e.target.value })}
              />
            </div>

            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Notes</Label>
              <Textarea
                rows={2}
                data-testid="asset-notes"
                className="mt-1.5"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" data-testid="asset-cancel" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="asset-submit" onClick={submit} disabled={submitting} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add asset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
